/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import {
  Button,
  Checkbox,
  GrabberIcon,
  Input,
  Modal,
  SearchIcon
} from "@forge/common";
import React, { useEffect, useState } from "react";
import { CustomizedColumnsProps } from "../../models";
import "./styles.scss";
import { DeviceListHeaderProps } from "../../models/devicelist";
import { validateInput } from "../../utils/utility";
import { useTranslation } from "react-i18next";
import { LOCALE_CONST } from "../../utils/constants";

const CustomizeColumns = (props: CustomizedColumnsProps) => {
  const { t } = useTranslation();

  // Initialize the columns from DeviceTableHeaders
  const [columns, setColumns] = useState<DeviceListHeaderProps[]>(
    props.tableHeaders.map((header: DeviceListHeaderProps) => ({
      id: header.id,
      content: header.content,
      ischecked: header.ischecked,
      canSort: header.canSort,
      className: header.className,
      removable: header.removable
    }))
  );
  const [draggedIndex, setDraggedIndex] = useState(0);
  const [allColumnsChecked, setAllColumnsChecked] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Handle dragging the columns
  const handleDragStart = (index: any) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: any) => {
    const newColumns = [...columns];
    const draggedItem = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1); // Remove dragged item
    newColumns.splice(index, 0, draggedItem); // Insert at the new position
    setDraggedIndex(index);
    setColumns(newColumns);
  };

  useEffect(() => {
    const allChecked = columns.every((col: any) => col.ischecked);
    setAllColumnsChecked(allChecked);
  }, [columns]);
  
  const handleCheckboxChange = (name: string) => {
    const newColumns = [...columns];
    const column = newColumns.find((col) => col.content === name);
    if (column) {
      column.ischecked = !column.ischecked;
      setColumns(newColumns);
    }
  };

  // Handle "Show all columns" checkbox
  const handleShowAllColumnsChange = () => {
    const allChecked = !allColumnsChecked;
    const newColumns = columns.map((col: any) => ({
      ...col,
      ischecked: col.removable ? allChecked : col.ischecked
    }));
    setAllColumnsChecked(allChecked);
    setColumns(newColumns);
  };

  const handleReset = () => {
    const resetColumns = props.initialTableHeaders.map(
      (header: DeviceListHeaderProps) => ({
        id: header.id,
        content: header.content,
        ischecked: header.ischecked,
        canSort: header.canSort,
        className: header.className,
        removable: header.removable
      })
    );
    setColumns(resetColumns);
    setAllColumnsChecked(false);
    setSearchQuery("");
  };

  const handleApply = () => {
    props.settableHeaders(columns);
    props.setClose(false);
  };

  const buttonsFooterRight = (
    <>
      <div className="showHideModep-footerContent-right">
        <Button
          variant="quiet-primary"
          className="mr-4 showHideModep-footerContent-right-reset"
          onClick={handleReset}
        >
          {t(LOCALE_CONST.reset)}
        </Button>

        <Button variant="primary" onClick={handleApply}>
          {t(LOCALE_CONST.apply)}
        </Button>
      </div>
    </>
  );

  // Filter columns based on search query
  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();
  const filteredColumns = columns.filter((header: any) =>
    header.content.toLowerCase().includes(normalizeSpaces(searchQuery.toLowerCase()))
  );
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (validateInput(value)) {
      setSearchQuery(value);
    }
  };


  return (
    <Modal
      backdrop
      onModalClose={() => props.setClose(false)}
      label={t(LOCALE_CONST.customizeColumns)}
      title={t(LOCALE_CONST.customizeColumnsSubText)}
      className="customize-columns"
      footerRight={buttonsFooterRight}
    >
      <div className="customize-columns-content mt-2">
        <div className="w-64">          
        <Input
          id="search"
          placeholder={t(LOCALE_CONST.searchColumns)}
          prefix={<SearchIcon strokeWidth={2} />}
          size="regular"
          onChange={handleInputChange}
          value={searchQuery}
        />
        </div>
        {filteredColumns.length > 0 && (
          <>
            <div className="pt-5">
              <Checkbox
                id="showAll"
                checked={allColumnsChecked}
                onChange={handleShowAllColumnsChange}
              >
                {t(LOCALE_CONST.showAllColumns)}
              </Checkbox>
            </div>
            <div className="mt-3">
              {filteredColumns.map(
                (header: DeviceListHeaderProps, index: number) => (
                  <>
                    {(header.content !== "Actions" && searchQuery === '') && (
                      <div
                        key={header.id}
                        className={`flex py-2 justify-between items-center ${
                          index === 0 && "border-b"
                        } ${
                          header.removable ? "cursor-move" : "pointer-events-none"
                        }`}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragOver={(e) => e.preventDefault()} // Prevent default to allow drop
                        data-testid={`column-${header.id}`}
                      >
                        <Checkbox
                          checked={header.ischecked}
                          onChange={() => handleCheckboxChange(header.content)}
                          id={`customize-column-${header.id}`}
                          name={header.id}
                          disabled={!header.removable}
                        >
                          {header.content} {header.removable}
                        </Checkbox>
                        <GrabberIcon className={`${(!header.removable || searchQuery !== '') ? 'grab-icon-disabled cursor-not-allowed' : 'cursor-move'}`} />
                      </div>
                    )}
                    {header.content !== "Actions" && searchQuery !== '' && (
                      <div
                        key={header.id}
                        className={`flex py-2 justify-between items-center`}                        
                        data-testid={`column-${header.id}`}
                      >
                        <Checkbox
                          checked={header.ischecked}
                          onChange={() => handleCheckboxChange(header.content)}
                          id={`customize-column-${header.id}`}
                          name={header.id}
                          disabled={!header.removable}
                        >
                          {header.content} {header.removable}
                        </Checkbox>
                        <GrabberIcon className="grab-icon-disabled cursor-not-allowed" />
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </>
        )}
        {filteredColumns.length <= 0 && <p className="pt-5">{t(LOCALE_CONST.noSearchDataFound)}</p>}
      </div>
    </Modal>
  );
};

export default CustomizeColumns;
